import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import PanToolIcon from "@mui/icons-material/PanTool";
import { useStatusPalette } from "../../../utils/statusColors";
import { STATUS_HUMAN_TAKEOVER } from "./conversationConstants";
import {
  buildStatusConfig,
  buildSessionEmailAddress,
  formatShareId,
} from "./conversationUtils";
import { pageHeaderMinSx } from "../../../constants/pageLayout";

function ChatHeader({
  session,
  agentInboxAddress,
  showSessionOperationalMeta = true,
  operatorView = true,
  allowHumanTakeoverControl = false,
  humanTakeoverBusy = false,
  onStartHumanTakeover,
  onEndHumanTakeover,
  leadingActions = null,
  actions = null,
}) {
  const _sp = useStatusPalette();
  const STATUS_CONFIG = buildStatusConfig(_sp);
  const st = STATUS_CONFIG[session.status] || STATUS_CONFIG.IN_PROGRESS;
  const isHumanTakeoverActive = session?.status === STATUS_HUMAN_TAKEOVER;
  const sessionEmail = showSessionOperationalMeta
    ? buildSessionEmailAddress(agentInboxAddress, session.emailThreadToken)
    : null;
  const isPublicShareSession = Boolean(
    operatorView &&
      showSessionOperationalMeta &&
      session?.source === "PUBLIC_CHAT" &&
      session?.publicShareId
  );
  const [copied, setCopied] = React.useState(false);
  const [shareIdCopied, setShareIdCopied] = React.useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    if (sessionEmail) {
      navigator.clipboard.writeText(sessionEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyShareId = (e) => {
    e.stopPropagation();
    if (!isPublicShareSession) return;
    navigator.clipboard.writeText(session.publicShareId);
    setShareIdCopied(true);
    setTimeout(() => setShareIdCopied(false), 2000);
  };

  return (
    <Box
      sx={{
        px: 2,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.subtle",
        gap: 1.5,
        flexShrink: 0,
        ...pageHeaderMinSx,
        flexWrap: "nowrap",
        overflow: "hidden",
      }}
    >
      {leadingActions && (
        <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
          {leadingActions}
        </Box>
      )}
      <Box
        sx={{
          minWidth: 0,
          flexShrink: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "2px",
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: "text.primary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            lineHeight: 1.2,
          }}
        >
          {session.title || "Untitled session"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              backgroundColor: st.color,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 500,
              color: st.color,
              lineHeight: 1,
              whiteSpace: "nowrap",
            }}
          >
            {st.label}
          </Typography>
        </Box>
      </Box>
      {sessionEmail && (
        <Box
          onClick={handleCopy}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            py: "4px",
            px: "10px",
            borderRadius: "6px",
            backgroundColor: copied ? _sp.greenAlt.bg : "action.hover",
            border: 1,
            borderColor: copied ? _sp.greenAlt.border : "divider",
            cursor: "pointer",
            transition: "all 150ms ease",
            "&:hover": {
              backgroundColor: copied ? _sp.greenAlt.bg : "action.selected",
            },
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 500,
              color: copied ? _sp.greenAlt.color : "text.secondary",
              lineHeight: 1.4,
              wordBreak: "break-all",
            }}
          >
            {sessionEmail}
          </Typography>
          {copied && (
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 600,
                color: _sp.greenAlt.color,
                lineHeight: 1,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              Copied!
            </Typography>
          )}
        </Box>
      )}
      {isPublicShareSession && (
        <Tooltip
          title={`Share ID: ${session.publicShareId}`}
          placement="top"
          arrow
        >
          <Box
            onClick={handleCopyShareId}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              py: "4px",
              px: "10px",
              borderRadius: "6px",
              backgroundColor: shareIdCopied ? _sp.greenAlt.bg : "action.hover",
              border: 1,
              borderColor: shareIdCopied ? _sp.greenAlt.border : "divider",
              cursor: "pointer",
              transition: "all 150ms ease",
              "&:hover": {
                backgroundColor: shareIdCopied
                  ? _sp.greenAlt.bg
                  : "action.selected",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 500,
                color: shareIdCopied ? _sp.greenAlt.color : "text.primary",
                lineHeight: 1.4,
                wordBreak: "break-all",
              }}
            >
              {shareIdCopied
                ? "Share ID copied"
                : `Shared link \u00b7 ${formatShareId(session.publicShareId)}`}
            </Typography>
          </Box>
        </Tooltip>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }} />
      <Box
        sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.75 }}
      >
        {allowHumanTakeoverControl && (
          <Tooltip
            title={
              humanTakeoverBusy
                ? "Updating..."
                : isHumanTakeoverActive
                ? "End takeover"
                : "Start takeover"
            }
            arrow
          >
            <IconButton
              size="small"
              disabled={humanTakeoverBusy}
              onClick={() =>
                isHumanTakeoverActive
                  ? onEndHumanTakeover?.()
                  : onStartHumanTakeover?.()
              }
              sx={{
                width: 28,
                height: 28,
                borderRadius: "8px",
                border: "1px solid",
                flexShrink: 0,
                ...(isHumanTakeoverActive
                  ? {
                      backgroundColor: _sp.teal.color,
                      borderColor: _sp.teal.color,
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: _sp.teal.color,
                        opacity: 0.85,
                      },
                    }
                  : {
                      borderColor: _sp.teal.border,
                      color: _sp.teal.color,
                      "&:hover": {
                        borderColor: _sp.teal.color,
                        backgroundColor: _sp.teal.bg,
                      },
                    }),
              }}
            >
              <PanToolIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        {actions}
      </Box>
    </Box>
  );
}

export default ChatHeader;
